/**
 * Application Constants
 * All constant values used throughout the application
 */

// Exam Subjects
export const SUBJECTS = {
  CIVIL_PROCEDURE: 'Civil Procedure',
  CONSTITUTIONAL_LAW: 'Constitutional Law',
  CRIMINAL_LAW: 'Criminal Law',
  CRIMINAL_PROCEDURE: 'Criminal Procedure',
  EVIDENCE: 'Evidence',
  PROPERTY: 'Property',
  TORTS: 'Torts',
  CONTRACTS: 'Contracts',
  FAMILY_LAW: 'Family Law',
  SECURED_TRANSACTIONS: 'Secured Transactions',
  WILLS_AND_TRUSTS: 'Wills and Trusts',
  PROFESSIONAL_RESPONSIBILITY: 'Professional Responsibility',
  CORPORATION: 'Corporation',
  AGENCY_PARTNERSHIP: 'Agency & Partnership',
  CONFLICT_OF_LAW: 'Conflict of Law',
};

export const SUBJECT_LIST = Object.values(SUBJECTS);

// Subject IDs mapping
export const SUBJECT_IDS = {
  [SUBJECTS.CIVIL_PROCEDURE]: 1,
  [SUBJECTS.CONSTITUTIONAL_LAW]: 2,
  [SUBJECTS.CRIMINAL_LAW]: 3,
  [SUBJECTS.CRIMINAL_PROCEDURE]: 4,
  [SUBJECTS.EVIDENCE]: 5,
  [SUBJECTS.PROPERTY]: 6,
  [SUBJECTS.TORTS]: 7,
  [SUBJECTS.CONTRACTS]: 8,
  [SUBJECTS.FAMILY_LAW]: 9,
  [SUBJECTS.SECURED_TRANSACTIONS]: 10,
  [SUBJECTS.WILLS_AND_TRUSTS]: 11,
  [SUBJECTS.PROFESSIONAL_RESPONSIBILITY]: 12,
  [SUBJECTS.CORPORATION]: 13,
  [SUBJECTS.AGENCY_PARTNERSHIP]: 14,
  [SUBJECTS.CONFLICT_OF_LAW]: 15,
};

// Exam Types
export const EXAM_TYPES = {
  MBE: 'mbe',
  MEE: 'mee',
  MPT: 'mpt',
};

// User Roles
export const USER_ROLES = {
  ADMIN: 'admin',
  EVALUATOR: 'evaluator',
  STUDENT: 'student',
  GUEST: 'guest',
};

// Subscription Tiers
export const SUBSCRIPTION_TIERS = {
  FREE: 'free',
  BASIC: 'basic',
  PREMIUM: 'premium',
  ENTERPRISE: 'enterprise',
};

// Sort Options
export const SORT_OPTIONS = {
  ALPHABET: { value: 'alphabet', label: '알파벳순' },
  RANDOM: { value: 'random', label: '랜덤순' },
  WRONG_FIRST: { value: 'wrong_first', label: '오답먼저' },
  RECENT: { value: 'recent', label: '최신순' },
  OLDEST: { value: 'oldest', label: '오래된순' },
  MOST_VIEWED: { value: 'most_viewed', label: '조회수순' },
  MOST_LIKED: { value: 'most_liked', label: '좋아요순' },
};

// Question Difficulty Levels
export const DIFFICULTY_LEVELS = {
  EASY: 'easy',
  MEDIUM: 'medium',
  HARD: 'hard',
  EXPERT: 'expert',
};

// Hint Levels
export const HINT_LEVELS = {
  NONE: 0,
  KEYWORDS: 1,
  DEFINITION: 2,
  FULL: 3,
};

// Hint Penalties (score reduction)
export const HINT_PENALTIES = {
  [HINT_LEVELS.NONE]: 0,
  [HINT_LEVELS.KEYWORDS]: 0.2,
  [HINT_LEVELS.DEFINITION]: 0.5,
  [HINT_LEVELS.FULL]: 0.8,
};

// Test Time Limits (in seconds)
export const TIME_LIMITS = {
  MBE_PER_QUESTION: 108, // 1.8 minutes per question
  MEE_PER_ESSAY: 1800, // 30 minutes per essay
  MPT_PER_TASK: 5400, // 90 minutes per task
};

// Scoring Thresholds
export const SCORING = {
  MBE_PASSING_SCORE: 70,
  MEE_PASSING_SCORE: 60,
  MPT_PASSING_SCORE: 60,
  PERFECT_SCORE: 100,
  EXCELLENT_THRESHOLD: 90,
  GOOD_THRESHOLD: 80,
  AVERAGE_THRESHOLD: 70,
  BELOW_AVERAGE_THRESHOLD: 60,
};

// Answer Options
export const ANSWER_OPTIONS = ['A', 'B', 'C', 'D'];

// Community Board Types
export const BOARD_TYPES = {
  FREE: 'free',
  NOTICE: 'notice',
  QNA: 'qna',
  STUDY: 'study',
  REVIEW: 'review',
  SUCCESS: 'success',
  CASE_BRIEF: 'case_brief',
};

// Post Status
export const POST_STATUS = {
  DRAFT: 'draft',
  PUBLISHED: 'published',
  ARCHIVED: 'archived',
  DELETED: 'deleted',
};

// Comment Status
export const COMMENT_STATUS = {
  PENDING: 'pending',
  APPROVED: 'approved',
  REJECTED: 'rejected',
  DELETED: 'deleted',
};

// Notification Types
export const NOTIFICATION_TYPES = {
  SUCCESS: 'success',
  ERROR: 'error',
  WARNING: 'warning',
  INFO: 'info',
};

// File Upload Limits
export const FILE_UPLOAD = {
  MAX_SIZE_MB: 10,
  MAX_SIZE_BYTES: 10 * 1024 * 1024, // 10MB
  ALLOWED_TYPES: ['image/jpeg', 'image/png', 'image/gif', 'application/pdf'],
  ALLOWED_EXTENSIONS: ['.jpg', '.jpeg', '.png', '.gif', '.pdf'],
};

// Pagination
export const PAGINATION = {
  DEFAULT_PAGE: 1,
  DEFAULT_PAGE_SIZE: 20,
  PAGE_SIZE_OPTIONS: [10, 20, 50, 100],
  MAX_PAGE_SIZE: 100,
};

// Cache Keys
export const CACHE_KEYS = {
  USER_PROFILE: 'user_profile',
  MBE_QUESTIONS: 'mbe_questions',
  MEE_QUESTIONS: 'mee_questions',
  MPT_QUESTIONS: 'mpt_questions',
  STUDY_PROGRESS: 'study_progress',
  DRAFT_ANSWERS: 'draft_answers',
  SETTINGS: 'settings',
};

// Local Storage Keys
export const STORAGE_KEYS = {
  AUTH_TOKEN: 'auth_token',
  USER_PREFERENCES: 'user_preferences',
  THEME: 'theme',
  LANGUAGE: 'language',
  DRAFT_PREFIX: 'draft_',
  CACHE_PREFIX: 'cache_',
};

// API Endpoints
export const API_ENDPOINTS = {
  AUTH: {
    LOGIN: '/auth/login',
    LOGOUT: '/auth/logout',
    SIGNUP: '/auth/signup',
    REFRESH: '/auth/refresh',
    VERIFY: '/auth/verify',
    RESET_PASSWORD: '/auth/reset-password',
  },
  USERS: {
    PROFILE: '/users/profile',
    UPDATE: '/users/update',
    DELETE: '/users/delete',
    SETTINGS: '/users/settings',
  },
  EXAMS: {
    MBE: '/exams/mbe',
    MEE: '/exams/mee',
    MPT: '/exams/mpt',
    SUBMIT: '/exams/submit',
    RESULTS: '/exams/results',
  },
  COMMUNITY: {
    POSTS: '/community/posts',
    COMMENTS: '/community/comments',
    LIKES: '/community/likes',
  },
};

// Regular Expressions
export const REGEX = {
  EMAIL: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  PASSWORD: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[a-zA-Z\d@$!%*?&]{8,}$/,
  USERNAME: /^[a-zA-Z0-9_]{3,20}$/,
  PHONE: /^\+?[1-9]\d{1,14}$/,
  URL: /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/,
};

// Date Formats
export const DATE_FORMATS = {
  DISPLAY: 'MMM DD, YYYY',
  DISPLAY_WITH_TIME: 'MMM DD, YYYY HH:mm',
  INPUT: 'YYYY-MM-DD',
  API: 'YYYY-MM-DDTHH:mm:ss.SSSZ',
  FILE_NAME: 'YYYYMMDD_HHmmss',
};

// Error Messages
export const ERROR_MESSAGES = {
  GENERIC: '오류가 발생했습니다. 다시 시도해주세요.',
  NETWORK: '네트워크 연결을 확인해주세요.',
  UNAUTHORIZED: '인증이 필요합니다. 다시 로그인해주세요.',
  FORBIDDEN: '접근 권한이 없습니다.',
  NOT_FOUND: '요청한 리소스를 찾을 수 없습니다.',
  VALIDATION: '입력한 정보를 확인해주세요.',
  SESSION_EXPIRED: '세션이 만료되었습니다. 다시 로그인해주세요.',
};

// Success Messages
export const SUCCESS_MESSAGES = {
  SAVED: '저장되었습니다.',
  UPDATED: '업데이트되었습니다.',
  DELETED: '삭제되었습니다.',
  SUBMITTED: '제출되었습니다.',
  COPIED: '복사되었습니다.',
  SENT: '전송되었습니다.',
};

// Export all constants as a single object for convenience
export const constants = {
  SUBJECTS,
  SUBJECT_LIST,
  SUBJECT_IDS,
  EXAM_TYPES,
  USER_ROLES,
  SUBSCRIPTION_TIERS,
  SORT_OPTIONS,
  DIFFICULTY_LEVELS,
  HINT_LEVELS,
  HINT_PENALTIES,
  TIME_LIMITS,
  SCORING,
  ANSWER_OPTIONS,
  BOARD_TYPES,
  POST_STATUS,
  COMMENT_STATUS,
  NOTIFICATION_TYPES,
  FILE_UPLOAD,
  PAGINATION,
  CACHE_KEYS,
  STORAGE_KEYS,
  API_ENDPOINTS,
  REGEX,
  DATE_FORMATS,
  ERROR_MESSAGES,
  SUCCESS_MESSAGES,
};